import { Dispatch } from '@reduxjs/toolkit';
import BigNumber from 'bignumber.js';
import { encodeFunctionData, hexToBigInt } from 'viem';

import stakingPoolAbi from 'modules/api/abi/DelegateStakingPool';
import poolOwnershipAbi from 'modules/api/abi/PoolOwnership';
import { IPFS_PREFIX } from 'modules/api/const';
import { getPublicClient } from 'modules/api/getPublicClient';
import { getPoolOwnershipAddress } from 'modules/api/methods/getPoolOwnershipAddress';
import { handleSafeAppTransaction } from 'modules/api/methods/handleSafeAppTransaction';
import { pinFileViaBff, pinJsonViaBff } from 'modules/api/methods/pinViaBff';
import { signPoolUploadAuth } from 'modules/api/methods/signPoolUploadAuth';
import { convertToWei } from 'modules/api/utils';
import { asAddress } from 'modules/api/utils/asAddress';
import { getChain } from 'modules/api/utils/getChain';
import { getWalletClient } from 'modules/api/walletClient';
import { IEditPoolArgs } from 'modules/ownerPanel/types';
import { getPool } from 'modules/pool/methods/getPool';
import { getPoolMeta } from 'modules/pool/methods/getPoolMeta';
import { IPoolRawMeta } from 'modules/pool/types';

export async function editPool(
  { poolAddress, name, description, image, comission }: IEditPoolArgs,
  dispatch: Dispatch,
): Promise<{ success: boolean }> {
  const walletClient = getWalletClient();
  const publicClient = getPublicClient();

  const accountAddress = asAddress(walletClient.account?.address);

  const pool = await getPool(publicClient, poolAddress);
  const poolMeta = await getPoolMeta(publicClient, poolAddress);

  const isMetaEqual =
    poolMeta.name === name && poolMeta.description === description && !image;

  const isComissionEqual = !!pool?.commission.isEqualTo(
    new BigNumber(comission),
  );

  if (!isMetaEqual) {
    // Prove pool ownership to the BFF once; reused for both uploads.
    const uploadAuth = await signPoolUploadAuth(poolAddress);

    const ipfsImageLink = await (async () => {
      if (!image) {
        return poolMeta.ipfsImageLink;
      }

      const response = await pinFileViaBff(image, uploadAuth);

      return `${IPFS_PREFIX}${response.IpfsHash}`;
    })();

    const rawMetadata: IPoolRawMeta = {
      name,
      description,
      image: ipfsImageLink,
      external_link: poolMeta.externalLink,
    };

    const response = await pinJsonViaBff(rawMetadata, uploadAuth);
    const ipfsDataLink = `${IPFS_PREFIX}${response.IpfsHash}`;

    const poolOwnershipAddress = await getPoolOwnershipAddress(publicClient);

    const encodedData = encodeFunctionData({
      abi: poolOwnershipAbi,
      functionName: 'setTokenURI',
      args: [hexToBigInt(poolAddress), ipfsDataLink],
    });

    const txPromise = walletClient.sendTransaction({
      to: poolOwnershipAddress,
      data: encodedData,
      chain: getChain(),
      account: accountAddress,
    });

    await handleSafeAppTransaction({
      txPromise,
      dispatch,
      publicClient,
      accountAddress,
    });
  }

  if (!isComissionEqual) {
    const encodedData = encodeFunctionData({
      abi: stakingPoolAbi,
      functionName: 'setFee',
      args: [convertToWei(comission, 2)],
    });

    const txPromise = walletClient.sendTransaction({
      to: poolAddress,
      data: encodedData,
      chain: getChain(),
      account: accountAddress,
    });

    await handleSafeAppTransaction({
      txPromise,
      dispatch,
      publicClient,
      accountAddress,
    });
  }

  return { success: true };
}
